import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Divider } from "@mui/material";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useChartPalette } from "../utils/chartUtils";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";
import { useAppTheme } from "@/hooks/useAppTheme";
import { currencyCompactNotation, formatUSD } from "@/utils/formatters/currencyFormatter";
import type { SpecificScatterPoint } from "@/types";

interface ClientSizeDeductibleChartProps {
  data: SpecificScatterPoint[];
}

/**
 * Scatter chart showing client size (group enrollment) vs specific deductible.
 * Reveals the correlation between client size and deductible amounts,
 * and highlights outliers that deviate from the trend.
 * Colored by carrier for additional context.
 */
export const ClientSizeDeductibleChart = ({ data: rawData }: ClientSizeDeductibleChartProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  const data = rawData
    .filter((d) => d.group_size > 0 && d.specific_deductible > 0)
    .map((d) => ({
      x: d.specific_deductible,
      y: d.group_size,
      carrier: d.carrier_name,
      client: d.client_name,
    }));

  if (data.length === 0) return <EmptyChartState />;

  const carriers = [...new Set(data.map((d) => d.carrier))];

  const getColor = (carrier: string) => {
    const index = carriers.indexOf(carrier);
    return chartPalette[index % chartPalette.length];
  };

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ScatterChart margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis
          type="number"
          dataKey="x"
          name="Specific Deductible"
          tickFormatter={(value) => formatUSD(value, currencyCompactNotation)}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
        />
        <YAxis type="number" dataKey="y" name="Client Size" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
        <Tooltip
          content={({ payload }) => {
            if (!payload || !payload.length) return null;
            const item = payload[0].payload;
            return (
              <div style={{ ...getChartTooltipStyle(theme), padding: "8px" }}>
                <strong>{item.client}</strong>
                <div>{item.carrier}</div>
                <Divider sx={{ my: 1 }} />
                <div>Specific Deductible: {formatUSD(item.x, currencyCompactNotation)}</div>
                <div>Group Size: {item.y.toLocaleString()}</div>
              </div>
            );
          }}
        />
        <Scatter data={data}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(entry.carrier)} />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};
